"use client";

import React from "react";
import { Wrench, Droplets, CircleAlert as AlertCircle, Activity, ArrowRight, ShieldCheck, PhoneCall, Clock, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface RepairService {
  id: string;
  title: string;
  category: string;
  description: string;
  commonSymptom: string;
  urgency: string;
  href: string;
}
const repairServices: RepairService[] = [{
  id: "toilet",
  title: "Toilet Repair",
  category: "Fixtures",
  description: "Rapid resolution for persistent running, severe clogs, base leaks, and weak flushing mechanisms.",
  commonSymptom: "Running continuously or leaking at the wax seal",
  urgency: "Same-Day Service",
  href: "/toilet-repair/"
}, {
  id: "faucet-sink",
  title: "Faucet & Sink Repair",
  category: "Fixtures",
  description: "Precision fixes for dripping taps, worn cartridges, low aeration flow, and loose basin mountings.",
  commonSymptom: "Persistent drip wasting water & staining sinks",
  urgency: "Fast Response",
  href: "/faucet-sink-repair/"
}, {
  id: "pipe-repair",
  title: "Pipe Repair",
  category: "Piping & Supply",
  description: "Immediate stabilization for burst pipes, pinhole copper leaks, corroded lines, and noisy water hammer.",
  commonSymptom: "Hissing behind walls or visible water spots",
  urgency: "Emergency Dispatch",
  href: "/pipe-repair/"
}, {
  id: "leak-detection",
  title: "Leak Detection & Repair",
  category: "Diagnostics",
  description: "Non-invasive electronic and acoustic locating to pinpoint hidden foundation and interior wall leaks.",
  commonSymptom: "Unexplained spike in monthly water bills",
  urgency: "Priority Priority",
  href: "/leak-detection-repair/"
}, {
  id: "disposal",
  title: "Garbage Disposal Repair",
  category: "Kitchen Plumbing",
  description: "Safe unjamming, motor troubleshooting, flywheel repairs, and water-tight under-sink resealing.",
  commonSymptom: "Humming motor with no spinning action",
  urgency: "Quick Diagnostic",
  href: "/garbage-disposal-repair/"
}, {
  id: "pressure",
  title: "Water Pressure Problems",
  category: "System Balance",
  description: "Full diagnostic of pressure-reducing valves (PRV), mineral build-up, and localized line restrictions.",
  commonSymptom: "Trickling showerheads or sudden line surges",
  urgency: "Comprehensive Check",
  href: "/problems/"
}];
const serviceHighlights = [{
  title: "Licensed & Insured",
  desc: "Certified Georgia master plumbers handling every repair."
}, {
  title: "Transparent Pricing",
  desc: "Upfront quotes with zero hidden emergency fees."
}, {
  title: "Fully Stocked Trucks",
  desc: "Most repairs completed on the very first visit."
}];
export default function PlumbingRepair() {
  const shouldReduceMotion = useReducedMotion();
  const getServiceIcon = (id: string) => {
    switch (id) {
      case "toilet":
        return <Wrench className="size-5 text-primary" />;
      case "faucet-sink":
        return <Droplets className="size-5 text-primary" />;
      case "pipe-repair":
        return <AlertCircle className="size-5 text-primary" />;
      case "leak-detection":
        return <Activity className="size-5 text-primary" />;
      case "disposal":
        return <Sparkles className="size-5 text-primary" />;
      case "pressure":
        return <Clock className="size-5 text-primary" />;
      default:
        return <Wrench className="size-5 text-primary" />;
    }
  };
  return <section id="generalplumbingrepair" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-18">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="flex justify-center mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground border border-border/60 gap-1.5">
              <Wrench className="size-3.5 text-accent" />
              Jonesboro Reliable Plumbing
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
             General Plumbing Repair
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.15
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Fast, diagnostic-driven plumbing repairs across Jonesboro, GA.
            Select your issue below for targeted troubleshooting and prompt dispatch.
          </motion.p>
        </div>

        {/* Triage Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {repairServices.map((service, index) => <motion.div key={service.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.06
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full bg-card text-card-foreground border border-border shadow-sm hover:shadow-md hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                <div>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center justify-center size-10 rounded-lg bg-primary/10 border border-primary/20 shrink-0">
                        {getServiceIcon(service.id)}
                      </div>
                      <Badge variant="outline" className="text-[11px] font-medium border-border/80 text-muted-foreground bg-background">
                        {service.urgency}
                      </Badge>
                    </div>

                    <div className="space-y-1">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {service.category}
                      </span>
                      <CardTitle className="text-xl font-bold text-card-foreground">
                        {service.title}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-1">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </CardDescription>

                    <div className="p-3 rounded-lg bg-muted/50 border border-border/60">
                      <p className="text-xs font-semibold text-foreground/80 mb-0.5">
                        Typical Issue:
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {service.commonSymptom}
                      </p>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-2 border-t border-border/50 flex items-center justify-between">
                  <Link to={service.href} className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
                    <span>Diagnose Problem</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                  <span className="text-xs text-muted-foreground">24/7 Available</span>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Feature Highlights Banner & Primary CTA */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} className="rounded-2xl bg-secondary/60 border border-border p-6 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Highlights */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {serviceHighlights.map((highlight, hIndex) => <div key={hIndex} data-index={hIndex} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-4 text-primary shrink-0" />
                    <h4 className="font-semibold text-sm text-foreground">
                      {highlight.title}
                    </h4>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {highlight.desc}
                  </p>
                </div>)}
            </div>

            {/* Right Action Trigger */}
            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col xl:flex-row items-stretch sm:items-center justify-end gap-3.5 pt-4 lg:pt-0 border-t lg:border-t-0 lg:border-l border-border lg:pl-8">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-sm w-full sm:w-auto">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border bg-background hover:bg-muted text-foreground font-semibold shadow-xs w-full sm:w-auto">
                <Link to="/problems/" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4 text-primary" />
                  <span>View All Problems</span>
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}